import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useMaintenanceCheck = () => {
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        // Check if maintenance mode is enabled
        const { data: settings } = await supabase
          .from('maintenance_settings')
          .select('enabled')
          .single();

        if (!settings?.enabled) {
          setShowMaintenance(false);
          setLoading(false);
          return;
        }

        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // Check for session-based bypass flag first (set after successful admin login)
          const hasBypassFlag = sessionStorage.getItem('admin_maintenance_bypass') === 'true';
          
          // Check if user has site_admin role
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .eq('role', 'site_admin')
            .maybeSingle();

          if (roleData) {
            // User is site_admin
            if (hasBypassFlag) {
              // Bypass flag is set - admin has completed full auth flow
              setShowMaintenance(false);
            } else {
              // No bypass flag - check MFA status
              const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
              
              // If user has MFA enrolled but hasn't completed AAL2, still show maintenance
              if (aalData?.nextLevel === 'aal2' && aalData?.currentLevel === 'aal1') {
                setShowMaintenance(true);
              } else {
                // Either no MFA required, or MFA already verified - bypass maintenance
                setShowMaintenance(false);
              }
            }
          } else {
            // Not a site_admin, clear any bypass flag and show maintenance
            sessionStorage.removeItem('admin_maintenance_bypass');
            setShowMaintenance(true);
          }
        } else {
          // Not authenticated, clear bypass flag and show maintenance
          sessionStorage.removeItem('admin_maintenance_bypass');
          setShowMaintenance(true);
        }
      } catch (error) {
        console.error('Error checking maintenance:', error);
        // On error, show maintenance to be safe
        setShowMaintenance(true);
      } finally {
        setLoading(false);
      }
    };

    checkMaintenance();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // Clear bypass flag on sign out
      if (event === 'SIGNED_OUT') {
        sessionStorage.removeItem('admin_maintenance_bypass');
      }
      checkMaintenance();
    });

    return () => subscription.unsubscribe();
  }, []);

  return { showMaintenance, loading };
};
